import React, { useEffect, useMemo, useRef, useState } from 'react';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';
import currencyFormatter from 'currency-formatter';
import { useTranslation } from 'react-i18next';
import { _Api } from '../../common/api/_call';
import { toFormatPrice } from '../../common/helpers/function';
import './MixMatchFilter.scss';

const emptyFilters = { style: [], shape: [], metal: [], price: { min: 0, max: 0 } };
const PLACEHOLDER_IMG =
  'https://gis247.s3.us-east-2.amazonaws.com/placeholder-blank.png';

const handleImgError = (e) => {
  if (e.currentTarget.src !== PLACEHOLDER_IMG) {
    e.currentTarget.onerror = null;
    e.currentTarget.src = PLACEHOLDER_IMG;
  }
};

// Pull the currency symbol from POS settings stored in localStorage.
// Mirrors the logic in src/pos/common/helpers/function.js (toFormatPrice).
const getCurrencySymbol = () => {
  try {
    const raw = localStorage.getItem('getPOSSettings');
    if (!raw) return '';
    const settings = JSON.parse(raw);
    const code = settings?.price_format?.currency;
    if (!code) return '';
    const found = currencyFormatter.findCurrency(code);
    return found?.symbol || code;
  } catch (err) {
    return '';
  }
};

const MixMatchFilter = ({
  itemId,
  itemName,
  type,
  onChange,
  itemOptions = [],
  onItemChange,
}) => {
  const { t } = useTranslation();
  const currencySymbol = useMemo(() => getCurrencySymbol(), []);
  const [filters, setFilters] = useState(emptyFilters);
  const [selectedStyle, setSelectedStyle] = useState([]);
  const [selectedShape, setSelectedShape] = useState([]);
  const [selectedMetal, setSelectedMetal] = useState([]);
  const [priceRange, setPriceRange] = useState([0, 0]);
  const [loaded, setLoaded] = useState(false);
  const [itemMenuOpen, setItemMenuOpen] = useState(false);
  const itemMenuRef = useRef(null);
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const selectedItemRow = useMemo(
    () => itemOptions.find((it) => String(it?._id) === String(itemId)),
    [itemOptions, itemId],
  );

  useEffect(() => {
    setItemMenuOpen(false);
  }, [itemId]);

  useEffect(() => {
    if (!itemMenuOpen) return undefined;
    const onDoc = (e) => {
      if (itemMenuRef.current && !itemMenuRef.current.contains(e.target)) {
        setItemMenuOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setItemMenuOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [itemMenuOpen]);

  useEffect(() => {
    let cancelled = false;
    const fetchFilters = async () => {
      if (!itemId) return;
      try {
        const res = await _Api(
          { item: itemId, type: type || 'inventory' },
          'api/v1/POS/customOrder/mixAndMatch/filterMixMatchProduct',
        );
        if (cancelled) return;
        if (res?.code === 200) {
          const f = res.filters || {};
          const next = {
            style: Array.isArray(f.style) ? f.style : [],
            shape: Array.isArray(f.shape) ? f.shape : [],
            metal: Array.isArray(f.metal) ? f.metal : [],
            price: {
              min: Number(f?.price?.min) || 0,
              max: Number(f?.price?.max) || 0,
            },
          };
          setFilters(next);
          setSelectedStyle([]);
          setSelectedShape([]);
          setSelectedMetal([]);
          setPriceRange([next.price.min, next.price.max]);
          setLoaded(true);
        }
      } catch (err) {
        console.log(err, 'err');
      }
    };
    fetchFilters();
    return () => {
      cancelled = true;
    };
  }, [itemId, type]);

  const toggle = (id, list, setList) => {
    const next = list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
    setList(next);
  };

  const clearStyle = () => setSelectedStyle([]);
  const clearShape = () => setSelectedShape([]);
  const clearMetal = () => setSelectedMetal([]);
  const clearPrice = () => setPriceRange([filters.price.min, filters.price.max]);

  const resetAll = () => {
    setSelectedStyle([]);
    setSelectedShape([]);
    setSelectedMetal([]);
    setPriceRange([filters.price.min, filters.price.max]);
    onChangeRef.current?.({
      style: [],
      shape: [],
      metal: [],
      price: { min: filters.price.min, max: filters.price.max },
    });
  };

  const submitFilters = () => {
    onChangeRef.current?.({
      style: selectedStyle,
      shape: selectedShape,
      metal: selectedMetal,
      price: { min: priceRange[0], max: priceRange[1] },
    });
  };

  const renderTile = (item, isActive, onClick) => (
    <button
      type="button"
      key={item.id}
      className={`mm-filter__tile${isActive ? ' is-active' : ''}`}
      onClick={onClick}
    >
      <span className="mm-filter__tile-img">
        <img
          src={item?.logo || PLACEHOLDER_IMG}
          alt={item?.name || ''}
          onError={handleImgError}
        />
      </span>
      <span className="mm-filter__tile-label">{item?.name || item?.code}</span>
    </button>
  );

  return (
    <div className="mm-filter">
      <div className="mm-filter__container">
        <div className="mm-filter__row mm-filter__row--top">
          <section className="mm-filter__col mm-filter__col--item">
            <h6 className="mm-filter__heading">{t('Choose an item')}</h6>
            {onItemChange && itemOptions?.length ? (
              <div
                className="mm-filter__item-dd"
                ref={itemMenuRef}
              >
                <button
                  type="button"
                  className="mm-filter__item-dd-trigger"
                  aria-label={t('Choose an item')}
                  aria-haspopup="listbox"
                  aria-expanded={itemMenuOpen}
                  onClick={() => setItemMenuOpen((o) => !o)}
                >
                  <span className="mm-filter__item-dd-thumb">
                    {selectedItemRow ? (
                      <img
                        src={selectedItemRow?.logo || PLACEHOLDER_IMG}
                        alt=""
                        onError={handleImgError}
                      />
                    ) : null}
                  </span>
                  <span className="mm-filter__item-dd-label">
                    {selectedItemRow?.name || t('Choose an item')}
                  </span>
                  <span
                    className={`mm-filter__item-dd-caret${itemMenuOpen ? ' is-open' : ''}`}
                    aria-hidden="true"
                  />
                </button>
                {itemMenuOpen ? (
                  <ul
                    className="mm-filter__item-dd-menu"
                    role="listbox"
                    aria-label={t('Choose an item')}
                  >
                    {itemOptions.map((it) => {
                      const isActive = String(it?._id) === String(itemId);
                      return (
                        <li key={String(it._id)} role="none">
                          <button
                            type="button"
                            role="option"
                            aria-selected={isActive}
                            className={`mm-filter__item-dd-option${isActive ? ' is-active' : ''}`}
                            onClick={() => {
                              onItemChange(it);
                              setItemMenuOpen(false);
                            }}
                          >
                            <span className="mm-filter__item-dd-thumb mm-filter__item-dd-thumb--option">
                              <img
                                src={it?.logo || PLACEHOLDER_IMG}
                                alt=""
                                onError={handleImgError}
                              />
                            </span>
                            <span className="mm-filter__item-dd-option-text">
                              {it?.name ?? ''}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                ) : null}
              </div>
            ) : (
              <div className="mm-filter__select" aria-disabled="true">
                <span className="mm-filter__select-value">{itemName || '-'}</span>
                <span className="mm-filter__select-caret" aria-hidden="true" />
              </div>
            )}
          </section>

          <section className="mm-filter__col">
            <div className="mm-filter__col-head">
              <h6 className="mm-filter__heading">{t('Setting Style')}</h6>
              {selectedStyle.length ? (
                <button type="button" className="mm-filter__clear" onClick={clearStyle}>
                  {t('Clear')}
                </button>
              ) : null}
            </div>
            <div className="mm-filter__list">
              {filters.style.length
                ? filters.style.map((s) =>
                    renderTile(s, selectedStyle.includes(s.id), () =>
                      toggle(s.id, selectedStyle, setSelectedStyle),
                    ),
                  )
                : null}
            </div>
          </section>

          <section className="mm-filter__col">
            <div className="mm-filter__col-head">
              <h6 className="mm-filter__heading">{t('Shape')}</h6>
              {selectedShape.length ? (
                <button type="button" className="mm-filter__clear" onClick={clearShape}>
                  {t('Clear')}
                </button>
              ) : null}
            </div>
            <div className="mm-filter__list">
              {filters.shape.length
                ? filters.shape.map((s) =>
                    renderTile(s, selectedShape.includes(s.id), () =>
                      toggle(s.id, selectedShape, setSelectedShape),
                    ),
                  )
                : null}
            </div>
          </section>
        </div>

        <div className="mm-filter__row mm-filter__row--bottom">
          <section className="mm-filter__col">
            <div className="mm-filter__col-head">
              <h6 className="mm-filter__heading">{t('Metal')}</h6>
              {selectedMetal.length ? (
                <button type="button" className="mm-filter__clear" onClick={clearMetal}>
                  {t('Clear')}
                </button>
              ) : null}
            </div>
            <div className="mm-filter__metal-grid">
              {filters.metal.length
                ? filters.metal.map((m) => {
                    const checked = selectedMetal.includes(m.id);
                    return (
                      <label key={m.id} className="mm-filter__check">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() =>
                            toggle(m.id, selectedMetal, setSelectedMetal)
                          }
                        />
                        <span className="mm-filter__check-box" aria-hidden="true" />
                        <span className="mm-filter__check-label">{m?.name || m?.code}</span>
                      </label>
                    );
                  })
                : null}
            </div>
          </section>

          <section className="mm-filter__col">
            <div className="mm-filter__col-head">
              <h6 className="mm-filter__heading">{t('Price')}</h6>
              {(priceRange[0] !== filters.price.min ||
                priceRange[1] !== filters.price.max) && (
                <button type="button" className="mm-filter__clear" onClick={clearPrice}>
                  {t('Clear')}
                </button>
              )}
            </div>
            <div className="mm-filter__price">
              <Slider
                range
                min={filters.price.min}
                max={filters.price.max || 1}
                value={priceRange}
                onChange={setPriceRange}
                disabled={!loaded || filters.price.max === 0}
              />
              <div className="mm-filter__price-fields">
                <div className="mm-filter__price-field">
                  <span className="mm-filter__price-currency">{currencySymbol}</span>
                  <span className="mm-filter__price-value">
                    {toFormatPrice(priceRange[0])}
                  </span>
                </div>
                <div className="mm-filter__price-field">
                  <span className="mm-filter__price-currency">{currencySymbol}</span>
                  <span className="mm-filter__price-value">
                    {toFormatPrice(priceRange[1])}
                  </span>
                </div>
              </div>
            </div>
          </section>
        </div>

        <div className="mm-filter__actions">
          <button
            type="button"
            className="mm-filter__reset"
            onClick={resetAll}
            disabled={!loaded}
          >
            {t('Reset')}
          </button>
          <button
            type="button"
            className="mm-filter__submit"
            onClick={submitFilters}
            disabled={!loaded}
          >
            {t('Submit Filter')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MixMatchFilter;
